// src/App.jsx
import React, { Suspense, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import ProtectedRoute from "./components/ProtectedRoute";
import { allRoutes } from "./routes/routeConfig";
import { componentMap } from "./routes/componentRegistry";

// إن كانت لديك هذه المكونات، أبقها كما هي:
import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";

/* ===================== Role-based Access ===================== */

// نفس سياسة الصلاحيات التي طلبتها للـ Sidebar:
const ROLES = {
  AHLAM: "ahlam",
  DEPO: "depo",
  SENIOR: "senior",
  PRODUCTION: "production",
};

// تعيين المسارات المسموحة لكل دور
const allowedPathsForRole = (role) => {
  if (!role) return new Set();

  // senior & ahlam: كل الصفحات
  if (role === ROLES.AHLAM || role === ROLES.SENIOR) {
    return new Set(allRoutes.filter(r => r.path).map(r => r.path));
  }

  // production: فقط معاملات المخزن
  if (role === ROLES.PRODUCTION) {
    return new Set(["/depo-transactions"]);
  }

  // depo: كل صفحات المخزن + صفحة أسماء المنتجات من الإعدادات
  if (role === ROLES.DEPO) {
    return new Set([
      "/packaging-materials",
      "/raw-materials",
      "/ready-materials",
      "/depo-transactions",
      "/product-names",
      "/raw-material-names",
      "/ready-material-names",
    ]);
  }

  return new Set();
};

// المسار الافتراضي بعد تسجيل الدخول حسب الدور
const defaultPathByRole = (role) => {
  if (role === ROLES.PRODUCTION) return "/depo-transactions";
  if (role === ROLES.DEPO) return "/raw-materials"; // اخترت الخام كبدء منطقي للمخزن
  if (role === ROLES.AHLAM || role === ROLES.SENIOR) return "/packaging-materials";
  // fallback عام
  return "/packaging-materials";
};

/* ===================== Utils ===================== */

const buildGeneratedRoutes = (routesList) =>
  routesList
    .filter((r) => r.path && r.componentName)
    .map((r) => {
      const Cmp = componentMap[r.componentName];
      if (!Cmp) return null;
      return <Route key={r.path} path={r.path} element={<Cmp />} />;
    });

/* ===================== App ===================== */

export default function App() {
  const { i18n } = useTranslation();
  const token =
    useSelector((s) => s.auth?.token) || localStorage.getItem("token");
  const darkMode = useSelector((s) => s.ui?.darkMode);
  const sidebarCollapsed = useSelector((s) => s.ui?.sidebarCollapsed);
  const currentUser = useSelector((s) => s.auth?.user);
  const role = currentUser?.role;

  // --- تطبيق الوضع الداكن على عنصر html ---
  useEffect(() => {
    const html = document.documentElement;
    if (darkMode) html.classList.add("dark");
    else html.classList.remove("dark");
  }, [darkMode]);

  // --- اتجاه اللغة (RTL/LTR) + lang على عنصر html ---
  useEffect(() => {
    const html = document.documentElement;
    if (i18n.language && i18n.language.startsWith("ar")) {
      html.setAttribute("dir", "rtl");
      html.setAttribute("lang", "ar");
    } else {
      html.setAttribute("dir", "ltr");
      html.setAttribute("lang", "en");
    }
  }, [i18n.language]);

  // المسارات المسموحة لهذا المستخدم
  const allowed = useMemo(() => allowedPathsForRole(role), [role]);

  // تقسيم المسارات (عام/خاص)
  const publicRoutes = useMemo(() => allRoutes.filter((r) => r.public), []);
  const privateRoutes = useMemo(
    () => allRoutes.filter((r) => !r.public && r.path && r.componentName),
    []
  );

  // تصفية المسارات الخاصة بناءً على الصلاحية
  const permittedPrivateRoutes = useMemo(
    () =>
      privateRoutes.filter((r) => {
        // senior/ahlam لديهم كل شيء بالفعل ضمن allowed
        return allowed.has(r.path);
      }),
    [privateRoutes, allowed]
  );

  // مسار الهبوط الافتراضي بعد تسجيل الدخول
  const defaultPath = useMemo(() => defaultPathByRole(role), [role]);

  // واجهة عامة مبسطة
  const PublicOnly = (
    <Routes>
      {buildGeneratedRoutes(publicRoutes)}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  // الواجهة المحمية (Navbar/Sidebar إن وجدت)
  const ProtectedShell = (
    <div className="h-screen bg-[var(--sidebar)] text-primary overflow-hidden flex flex-col">
      <Navbar />
      <div className="flex flex-1 overflow-hidden">
        {!sidebarCollapsed && <Sidebar currentUser={currentUser} />}
        <main className="flex-1 overflow-auto">
          <Routes>
            {/* حماية عامة */}
            <Route element={<ProtectedRoute />}>
              {/* توجيه الهبوط بعد تسجيل الدخول بناءً على الدور */}
              <Route
                path="/"
                element={<Navigate to={defaultPath} replace />}
              />

              {/* منع الوصول لمسار غير مسموح به: إذا حاول فتح شيء خارج allowed، وجّهه لأول مسار مسموح */}
              {/* نبني جميع المسارات الخاصة المسموحة فقط */}
              {buildGeneratedRoutes(permittedPrivateRoutes)}

              {/* 404 خاص داخل الشل: رجّع المستخدم للمسار الافتراضي لدوره */}
              <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Route>
          </Routes>
        </main>
      </div>
    </div>
  );

  return (
    <Suspense fallback={<div style={{ padding: 24 }}>Loading...</div>}>
      {token ? ProtectedShell : PublicOnly}
    </Suspense>
  );
}
